import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";

export function Card({ title, subtitle, right, children, testId }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      {(title || right) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 sm:px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

export function useLoad(url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get(url, { params })).data); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setLoading(false); }
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load, setData };
}

export async function act(fn, okMsg) {
  try {
    const r = await fn();
    if (okMsg) toast.success(okMsg);
    return r?.data ?? true;
  } catch (e) {
    const m = apiErrorMessage(e);
    if (m) toast.error(m);
    return null;
  }
}

export const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—");
export const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
export const cleanLabel = (s) => String(s || "").replace(/\s+/g, " ");

export function Badge({ tone = "slate", children, testId }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600", blue: "bg-[#327de3]/10 text-[#1f64c2]", green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700", red: "bg-rose-50 text-rose-700",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`} data-testid={testId}>{children}</span>;
}
